// deps
import React, { ReactElement, useMemo, useCallback, ReactNode, memo, useContext, useRef, useState } from "react";

// internal-deps
import { CheckoutRoute, useNavigate, UserContext, AnalyticsContext } from "@swiggy-private/checkout-core";
import { getCloudinaryUrl, useEffectOnce } from "@swiggy-private/core-ui";
import { PAYLATER_TYPES } from "@swiggy-private/payment-types";
import { LinkAccountPhoneNumber } from "@swiggy-private/payment-ui";

// interfaces
import { RouteProps } from "@payment-client/interfaces/Router";

// components
import CommonUIWrapper from "@payment-client/components/common-ui-wrapper/CommonUIWrapper";
import CommonHeaderContent from "@payment-client/components/common-header-content/CommonHeaderContent";

//hooks
import { useGetPayLaterData } from "@payment-client/hooks/UseGetPayLaterData";

//helpers
import { getLinkPayLaterPageTitle } from "@payment-client/helpers/PaylaterHelper";

//analytics
import {
    clickProceedLinkPayLaterEvent,
    linkAccountWithNumberScreenViewEvent,
    impressionConfirmProceedEvent,
} from "@payment-client/analytics/events/LinkPayLater";
import { CONTEXT } from "@payment-client/analytics/constants";

//constants
import { PAYLATER_DISPLAY_NAMES } from "@swiggy-private/payment-core";

type PaymentLinkPayLaterProps = RouteProps<{
    payLaterCode: string;
}>;

const LinkPayLaterWithNumberWrapper = function VerifyWalletLinkOtpWrapper(props: {
    children: ReactNode;
    title: string;
}): ReactElement {
    const { title, children } = props;

    const header = <CommonHeaderContent title={title} />;
    return <CommonUIWrapper header={header} content={children} />;
};

function LinkPayLaterWithNumber({ data }: PaymentLinkPayLaterProps): ReactElement | null {
    const navigateTo = useNavigate();
    const payLaterCode = (data?.payLaterCode as PAYLATER_TYPES) || "";
    const { user } = useContext(UserContext);
    const registeredMobileNumber = user.map(user => user.mobile);
    const { clickEvent, impressionEvent, screenViewEvent } = useContext(AnalyticsContext);
    const navigatingToVerifyLinkWalletOtp = useRef(false);

    const { payLaterMethodData } = useGetPayLaterData(payLaterCode);

    const [mobileNumber, setMobileNumber] = useState<string>(registeredMobileNumber.unwrap());

    const onConfirmLinking = useCallback(
        async (mobileNumber: string) => {
            clickProceedLinkPayLaterEvent(payLaterCode, CONTEXT.LINK_WALLET_SOURCE_PAYMENT, clickEvent);
            navigatingToVerifyLinkWalletOtp.current = true;
            navigateTo(CheckoutRoute.LINK_PAYLATER, {
                payLaterCode,
                mobileNumber,
                iconUrl: payLaterMethodData?.meta.iconUrl,
                paymentCode: payLaterMethodData?.paymentCode,
            });
        },
        [clickEvent, navigateTo, payLaterCode, payLaterMethodData],
    );

    const title = useMemo(() => getLinkPayLaterPageTitle(payLaterCode), [payLaterCode]);

    const editableState = useMemo(() => {
        return {
            isEditable: true,
            registeredMobileNumber: registeredMobileNumber.unwrapOr(""),
        };
    }, [registeredMobileNumber]);

    useEffectOnce(() => {
        const linkWalletWithNumberSource = CONTEXT.LINK_WALLET_SOURCE_PAYMENT;

        linkAccountWithNumberScreenViewEvent(payLaterCode, linkWalletWithNumberSource, true, screenViewEvent);
        impressionConfirmProceedEvent(payLaterCode, linkWalletWithNumberSource, impressionEvent);
    });

    return (
        <LinkPayLaterWithNumberWrapper title={title}>
            <LinkAccountPhoneNumber
                onConfirmLinking={onConfirmLinking}
                iconUrl={getCloudinaryUrl(payLaterMethodData?.meta.iconUrl) || ""}
                displayName={PAYLATER_DISPLAY_NAMES[payLaterCode]}
                editableState={editableState}
                mobileNumber={mobileNumber}
                setMobileNumber={setMobileNumber}
            />
        </LinkPayLaterWithNumberWrapper>
    );
}

export default memo(LinkPayLaterWithNumber);
